import { api } from './client';

// ── Reply templates ─────────────────────────────────────────────────────────
export interface ReplyTemplate {
  id: string;
  title: string;
  body: string;
  is_shared: boolean;
  owner_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export const replyTemplatesApi = {
  list: () => api.get<ReplyTemplate[]>('/api/v1/reply-templates').then(r => r.data),
  create: (p: { title: string; body: string; is_shared?: boolean }) =>
    api.post<ReplyTemplate>('/api/v1/reply-templates', p).then(r => r.data),
  update: (id: string, p: { title: string; body: string; is_shared?: boolean }) =>
    api.patch<ReplyTemplate>(`/api/v1/reply-templates/${id}`, p).then(r => r.data),
  remove: (id: string) => api.delete(`/api/v1/reply-templates/${id}`),
};

// ── Redmine escalation ──────────────────────────────────────────────────────
export interface EscalateOut {
  redmine_issue_id: number;
  redmine_url: string;
  ticket_id: string;
  support_line: number;
}

export interface RedmineProject { id: number; identifier: string; name: string }

export const redmineApi = {
  escalate: (ticket_id: string, p: { project_id?: string; tracker_id?: string; note?: string; assigned_to_id?: number } = {}) =>
    api.post<EscalateOut>(`/api/v1/tickets/${ticket_id}/escalate-redmine`, p).then(r => r.data),
  projects: () =>
    api.get<{ projects: RedmineProject[]; default: string }>(`/api/v1/redmine/projects`).then(r => r.data),
  syncNow: (ticket_id: string) =>
    api.post<{ changed: boolean; new_messages?: number; status_changed?: boolean }>(
      `/api/v1/tickets/${ticket_id}/sync-redmine`,
    ).then(r => r.data),
};

// ── Similar tickets ─────────────────────────────────────────────────────────
export interface SimilarTicket {
  id: string;
  number: number;
  subject: string;
  status: string;
  priority: string;
  organization_name: string | null;
  score: number;
  snippet: string | null;
}

export const similarApi = {
  get: (ticket_id: string) =>
    api.get<SimilarTicket[]>(`/api/v1/tickets/${ticket_id}/similar`).then(r => r.data),
};

// ── Analytics dashboard ─────────────────────────────────────────────────────
export interface Dashboard {
  period: { days: number; from: string; to: string };
  totals: { created: number; resolved: number; closed: number; breach_frt: number; breach_ttr: number };
  by_priority: Record<string, number>;
  by_line: Record<string, number>;
  by_status: Record<string, number>;
  avg_frt_minutes: number | null;
  avg_ttr_minutes: number | null;
  top_agents: Array<{ user_id: string; name: string; resolved: number }>;
  top_organizations: Array<{ organization_id: string; name: string; created: number; breach: number; score: number }>;
}

export interface WeeklyPoint {
  week: string;
  week_start: string;
  created: number;
  resolved: number;
  closed: number;
  breach: number;
}

export interface AgentRating {
  user_id: string;
  name: string;
  email: string;
  resolved: number;
  closed: number;
  breach: number;
  avg_frt_minutes: number | null;
  avg_ttr_minutes: number | null;
  avg_rating: number | null;
  rating_count: number;
  score: number;
}

export interface EscalationRow {
  key: string;
  label: string;
  count: number;
  extra?: { email?: string; url?: string } | null;
}
export interface EscalationsOut {
  period_days: number;
  total_escalations: number;
  total_tickets: number;
  rate: number;
  by_organization: EscalationRow[];
  by_assignee: EscalationRow[];
  by_priority: EscalationRow[];
  by_support_line: EscalationRow[];
  by_status: EscalationRow[];
}

export interface AgentSla {
  user_id: string;
  name: string;
  email: string;
  assigned: number;
  resolved: number;
  closed: number;
  frt_breach: number;
  ttr_breach: number;
  frt_breach_rate: number;
  ttr_breach_rate: number;
  avg_frt_minutes: number | null;
  avg_ttr_minutes: number | null;
  avg_rating: number | null;
  rating_count: number;
}

export const analyticsApi = {
  dashboard: (days = 30) =>
    api.get<Dashboard>('/api/v1/analytics/dashboard', { params: { days } }).then(r => r.data),
  weekly: (weeks = 52, agent_id?: string) =>
    api.get<WeeklyPoint[]>('/api/v1/analytics/weekly',
      { params: { weeks, agent_id } }).then(r => r.data),
  agentsRating: (days = 30) =>
    api.get<{ period_days: number; most_active: AgentRating[]; least_effective: AgentRating[] }>(
      '/api/v1/analytics/agents-rating', { params: { days } }).then(r => r.data),
  agentsSla: (days = 30) =>
    api.get<{ period_days: number; items: AgentSla[] }>(
      '/api/v1/analytics/agents-sla', { params: { days } }).then(r => r.data),
  escalations: (days = 30) =>
    api.get<EscalationsOut>(
      '/api/v1/analytics/escalations', { params: { days } }).then(r => r.data),
  aiReport: (days = 30, agent_id?: string) =>
    api.post<{ text: string; metrics_summary: any }>(
      '/api/v1/analytics/ai-report', null,
      { params: { days, agent_id } }).then(r => r.data),
};
